import React, { useState, useEffect } from 'react';
import { sendRequest } from '../../api';
import ContentCard from './ContentCard';
import TrendChart from './TrendChart';
import ThinkingAgentView from './ThinkingAgentView';
import AgentActivityFeed from './AgentActivityFeed';
import './TopicDetail.css'; // Add CSS file for animations

const TopicDetail = ({ topic, onBack, onDeleteTopic, isNewlyCreated }) => {
  const [content, setContent] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [currentTopic, setCurrentTopic] = useState(topic);
  
  // Check if thinking mode is enabled
  const isThinkingMode = topic?.config?.thinking_mode === true;

  useEffect(() => {
    // Normal fetch
    fetchTopicData();
    
    // Set up polling to check for topic updates (including new monitored URLs)
    const topicPollingInterval = setInterval(fetchTopicDetails, 30000); // Check every 30 seconds
    
    return () => {
      if (topicPollingInterval) {
        clearInterval(topicPollingInterval);
      }
    };
    // eslint-disable-next-line
  }, [topic.id]);

  // Update current topic when prop changes
  useEffect(() => {
    setCurrentTopic(topic);
  }, [topic]);

  const fetchTopicData = async () => {
    setLoading(true);
    
    try {
      const [contentRes, trendsRes] = await Promise.all([
        sendRequest(`/topics/${topic.id}/content`, 'GET'),
        sendRequest(`/topics/${topic.id}/trends?period=24h`, 'GET')
      ]);
      
      // Ensure content items have proper default values for maps
      let processedContent = (contentRes || []).map(item => ({
        ...item,
        engagement_metrics: item.engagement_metrics || {},
        entities: item.entities || {},
        metadata: item.metadata || {},
        author_metadata: item.author_metadata || {},
        media_metadata: item.media_metadata || {},
        media_urls: item.media_urls || []
      }));
      
      // Smart sorting based on topic intent and recency
      processedContent = sortContentByIntent(processedContent);
      
      setContent(processedContent);
      setTrends(trendsRes || []);
      setError(null);
      
      // Return true if we found content
      return processedContent.length > 0;
    } catch (err) {
      console.error('Error fetching topic data:', err);
      setError('Failed to load topic data');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Smart content sorting based on topic intent and recency
  const sortContentByIntent = (content) => {
    const intent = topic?.config?.agent_intent || '';
    const isTrendTracking = ['trend_analysis', 'sentiment_tracking', 'discovery', 'competitive_intelligence'].includes(intent);
    const now = new Date();
    
    return content.sort((a, b) => {
      const aDate = new Date(a.published_at || a.discovered_at);
      const bDate = new Date(b.published_at || b.discovered_at);
      const aHoursAgo = (now - aDate) / (1000 * 60 * 60);
      const bHoursAgo = (now - bDate) / (1000 * 60 * 60);
      
      // For trend tracking intents, heavily prioritize recent content
      if (isTrendTracking) {
        // Very recent content (last 6 hours) gets highest priority
        const aVeryRecent = aHoursAgo <= 6;
        const bVeryRecent = bHoursAgo <= 6;
        
        if (aVeryRecent && !bVeryRecent) return -1;
        if (!aVeryRecent && bVeryRecent) return 1;
        
        // Recent content (last 24 hours) gets second priority
        const aRecent = aHoursAgo <= 24;
        const bRecent = bHoursAgo <= 24;
        
        if (aRecent && !bRecent) return -1;
        if (!aRecent && bRecent) return 1;
        
        // Within same recency tier, sort by relevance then recency
        if (Math.abs((a.relevance_score || 0) - (b.relevance_score || 0)) > 0.1) {
          return (b.relevance_score || 0) - (a.relevance_score || 0);
        }
        
        // Finally by recency
        return aDate > bDate ? -1 : 1;
      }
      
      // For other intents, balance relevance and recency
      const aRelevance = a.relevance_score || 0;
      const bRelevance = b.relevance_score || 0;
      
      // If relevance scores are significantly different, use relevance
      if (Math.abs(aRelevance - bRelevance) > 0.2) {
        return bRelevance - aRelevance;
      }
      
      // If relevance is similar, prefer recent content
      if (aHoursAgo <= 24 && bHoursAgo > 24) return -1;
      if (aHoursAgo > 24 && bHoursAgo <= 24) return 1;
      
      // Within same time window, use recency
      return aDate > bDate ? -1 : 1;
    });
  };

  // Helper function to determine content recency category
  const getContentRecencyInfo = (publishedAt) => {
    if (!publishedAt) return { category: 'unknown', label: '', className: '' };
    
    const now = new Date();
    const published = new Date(publishedAt);
    const hoursAgo = (now - published) / (1000 * 60 * 60);
    
    if (hoursAgo <= 1) {
      return { 
        category: 'breaking', 
        label: '🔥 Breaking', 
        className: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 animate-pulse' 
      };
    } else if (hoursAgo <= 6) {
      return { 
        category: 'very-recent', 
        label: '⚡ Very Recent', 
        className: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200' 
      };
    } else if (hoursAgo <= 24) {
      return { 
        category: 'recent', 
        label: '🔔 Recent', 
        className: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' 
      };
    } else if (hoursAgo <= 72) {
      return { 
        category: 'fresh', 
        label: '📅 Fresh', 
        className: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
      };
    }
    
    return { category: 'older', label: '', className: '' };
  };

  const refreshContent = () => {
    fetchTopicData();
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this topic? This action cannot be undone.')) return;
    try {
      await onDeleteTopic(topic.id);
      onBack();
    } catch (err) {
      alert('Failed to delete topic');
    }
  };

  // Generic context extraction for monitoring sources
  const extractSourceContext = (sourceUrl) => {
    try {
      const url = new URL(sourceUrl);
      const pathParts = url.pathname.split('/').filter(part => part.length > 0);
      
      // Common URL patterns across different sites
      const patterns = [
        // Reddit: /r/subreddit/...
        { pattern: /^r$/, next: true, prefix: 'r/' },
        // YouTube: /c/channel or /user/username
        { pattern: /^(c|user|channel)$/, next: true, prefix: '📺 ' },
        // Twitter/X: /username
        { pattern: /^[a-zA-Z0-9_]+$/, condition: (parts, i) => i === 0 && (url.hostname.includes('twitter') || url.hostname.includes('x.com')), prefix: '@' },
        // Medium: /@username or /publication
        { pattern: /^@/, prefix: '' },
        // Generic blog/news: /category/ or /section/
        { pattern: /^(category|section|tag|topic|blog)$/, next: true, prefix: '' },
        // Forums: /forum/name or /community/name
        { pattern: /^(forum|community|group)$/, next: true, prefix: '👥 ' },
      ];
      
      for (let i = 0; i < pathParts.length; i++) {
        const part = pathParts[i];
        
        for (const { pattern, next, condition, prefix } of patterns) {
          if (pattern.test(part)) {
            // Check condition if provided
            if (condition && !condition(pathParts, i)) continue;
            
            if (next && i + 1 < pathParts.length) {
              const contextPart = pathParts[i + 1];
              // Skip common non-contextual parts
              if (!['posts', 'comments', 'about', 'wiki', 'rules'].includes(contextPart)) {
                return `${prefix}${contextPart}`;
              }
            } else if (!next) {
              return `${prefix}${part}`;
            }
          }
        }
      }
      
      // Fallback: use first meaningful path segment
      if (pathParts.length > 0 && pathParts[0].length > 2) {
        const firstPart = pathParts[0];
        // Skip common non-contextual paths
        if (!['api', 'www', 'en', 'news', 'article', 'post', 'blog'].includes(firstPart)) {
          return firstPart;
        }
      }
      
    } catch (e) {
      // URL parsing failed
    }
    
    return null;
  };

  const fetchTopicDetails = async () => {
    try {
      const response = await sendRequest(`/topics/${topic.id}`, 'GET');
      setCurrentTopic(response);
    } catch (err) {
      console.error('Error fetching topic details:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <button
              onClick={onBack}
              className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to topics
            </button>
            
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  {currentTopic.name}
                </h1>
                {currentTopic.description && (
                  <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl">
                    {currentTopic.description}
                  </p>
                )}
                
                {/* Tags */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {currentTopic.query_terms && currentTopic.query_terms.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {currentTopic.query_terms.map((term, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-sm"
                        >
                          {term}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {currentTopic.config?.agent_intent && (
                    <span className="inline-flex items-center px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-sm">
                      🤖 {currentTopic.config.agent_intent.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  )}
                </div>

                {/* Monitored URLs from AI Conversations */}
                {currentTopic.monitored_urls && currentTopic.monitored_urls.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      📌 URLs added through conversations:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {currentTopic.monitored_urls.map((url, idx) => {
                        try {
                          const urlObj = new URL(url);
                          const domain = urlObj.hostname.replace('www.', '');
                          const favicon = `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
                          
                          return (
                            <a
                              key={idx}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg text-sm hover:bg-green-100 dark:hover:bg-green-900/50 transition-all group border border-green-200 dark:border-green-700"
                              title={`Visit ${domain}`}
                            >
                              <img 
                                src={favicon} 
                                alt={domain}
                                className="w-4 h-4 flex-shrink-0"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'inline';
                                }}
                              />
                              <span className="text-green-600 dark:text-green-400 text-xs" style={{display: 'none'}}>🔗</span>
                              <span className="font-medium truncate max-w-[200px]">{domain}</span>
                              <svg className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          );
                        } catch (e) {
                          // Fallback for invalid URLs
                          return (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm border border-gray-200 dark:border-gray-600"
                            >
                              🔗 {url.length > 30 ? url.substring(0, 30) + '...' : url}
                            </span>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}

                {/* Sources from Topic Configuration */}
                {currentTopic.sources && currentTopic.sources.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      🔍 Configured sources:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {currentTopic.sources.map((source, idx) => (
                        <a
                          key={idx}
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-sm hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-all group border border-blue-200 dark:border-blue-700"
                          title={`Visit ${source.metadata?.domain || source.url}`}
                        >
                          {source.metadata?.logo && (
                            <img 
                              src={source.metadata.logo} 
                              alt={source.metadata.domain}
                              className="w-4 h-4 flex-shrink-0"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          )}
                          <span className="font-medium truncate max-w-[200px]">
                            {source.metadata?.title || source.metadata?.domain || 'Source'}
                          </span>
                          {source.metadata?.context && (
                            <span className="text-xs opacity-75">({source.metadata.context})</span>
                          )}
                          <svg className="w-3 h-3 opacity-60 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2 ml-4">
                <span className={`px-3 py-1 text-sm rounded-full ${
                  currentTopic.status === 'active' 
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}>
                  {currentTopic.status || 'active'}
                </span>
                
                <button
                  onClick={handleDelete}
                  className="p-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 
                           bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  title="Delete topic"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Agent Activity Feed */}
      <div className="py-8">
        <AgentActivityFeed topicId={currentTopic.id} topicName={currentTopic.name} />
      </div>
    </div>
  );
};

export default TopicDetail;
